import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';

const RecentImagesGallery = ({ projects = [] }) => {
  const scrollRef = useRef(null);
  const [previewContent, setPreviewContent] = useState(null);

  // Extrair todas as imagens/vídeos completos dos projetos
  const allContents = projects
    .flatMap(project => 
      (project.contents || [])
        .filter(c => c.status === 'completed' && c.result_url)
        .map(c => ({ ...c, projectTitle: project.title, projectId: project.id }))
    )
    .slice(0, 30);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (allContents.length === 0) {
    return null;
  }

  return (
    <>
      <div className="relative group">
        {/* Scroll buttons */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white border border-gray-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
        >
          <ChevronLeft className="w-4 h-4 text-gray-700" />
        </button>
        
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white border border-gray-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
        >
          <ChevronRight className="w-4 h-4 text-gray-700" />
        </button>

        {/* Gallery scroll container */}
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto scrollbar-hide p-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {allContents.map((content, idx) => (
            <div
              key={content.id || idx}
              onClick={() => setPreviewContent(content)}
              className="flex-shrink-0 w-52 h-52 rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-amber-400 hover:scale-[1.02] transition-all relative group/item bg-gray-100 shadow-sm"
            >
              {content.type === 'video' ? (
                <>
                  <video
                    src={content.result_url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    onMouseEnter={(e) => e.target.play()}
                    onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover/item:opacity-100 transition-opacity">
                    <Play className="w-6 h-6 text-white" fill="white" />
                  </div>
                </>
              ) : (
                <img
                  src={content.result_url}
                  alt={`Content ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {previewContent && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewContent(null)}
        >
          <button
            onClick={() => setPreviewContent(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div 
            className="max-w-4xl w-full max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {previewContent.type === 'video' ? (
              <video
                src={previewContent.result_url}
                controls
                autoPlay
                className="max-w-full max-h-[75vh] rounded-lg"
              />
            ) : (
              <img
                src={previewContent.result_url}
                alt="Preview"
                className="max-w-full max-h-[75vh] rounded-lg object-contain"
              />
            )}
            
            {previewContent.projectTitle && (
              <p className="mt-3 text-sm text-white/70">
                {previewContent.projectTitle}
              </p>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default RecentImagesGallery;